import { useQuery } from "@apollo/client";
import { MustBeCompany } from "core/routePolicies";
import { useGuard } from "hooks/useGuard";
import { getCurrencyList } from "models/user/selectors";
import { ColumnDescription } from "pages/dealflow/InvestorDealflow/interfaces";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  DealflowColumn,
  DealflowRecord,
  GetDealflow,
  GetDealflowRecords,
} from "./interfaces";
import {
  GET_DEALFLOW,
  GET_DEALFLOW_RECORDS,
} from "queries/startup/startupDealflow";
import { COLUMNS_LIST } from "./table";
import { InvestorType } from "pages/companySettings/steps/FundraisingStep/Investors";

const FIRST_PAGE = 1;

const populateColumn = (column: DealflowColumn): ColumnDescription =>
  COLUMNS_LIST.find((item) => item.key === column.key) || column;

const populateColumns = (columns: DealflowColumn[]) =>
  columns.map((column) => populateColumn(column));

const useDealflow = () => {
  useGuard([MustBeCompany]);
  const [isAddModal, setIsAddModal] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [filterSections, setFilterSections] = useState<string[]>([]);
  const [filterInvestorType, setFilterInvestorType] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "createdAt", order: -1 });
  const {
    data: dealflowData,
    loading: isDealflowLoading,
    refetch: refetchDealflow,
  } = useQuery<GetDealflow>(GET_DEALFLOW);

  const [editRecord, setEditRecord] = useState<DealflowRecord>();

  const {
    data: companiesData,
    previousData: companiesPreviousData,
    loading: isCompaniesLoading,
    refetch,
  } = useQuery<GetDealflowRecords>(GET_DEALFLOW_RECORDS, {
    variables: {
      page,
      query,
      sort,
      sections: filterSections,
      investorTypes: filterInvestorType,
    },
    fetchPolicy: "cache-and-network",
  });
  const currencies = useSelector(getCurrencyList);
  const [selectedRecord, setSelectedRecord] = useState<DealflowRecord>();
  const [previewRecord, setPreviewRecord] = useState<DealflowRecord>();

  useEffect(() => {
    if (!companiesData?.dealflow.records || !selectedRecord) {
      return;
    }

    const newValue = companiesData.dealflow.records.nodes.find(
      (record) => record.id === selectedRecord.id
    );

    if (newValue) {
      setSelectedRecord(newValue);
    }
  }, [companiesData?.dealflow.records, selectedRecord]);

  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery);
    setPage(FIRST_PAGE);
  };

  const handleSelect = (record: DealflowRecord) => {
    setSelectedRecord(record);
    setPreviewRecord(record);
  };

  const handleSectionsChange = (newSections: typeof filterSections) => {
    setFilterSections(newSections);
    setPage(FIRST_PAGE);
  };

  const handleInvestorTypeChange = (newTypes: typeof filterInvestorType) => {
    setFilterInvestorType(newTypes);
    setPage(FIRST_PAGE);
  };

  const handleCloseSidebar = () => setSelectedRecord(undefined);

  const handleClosePreview = () => setPreviewRecord(undefined);

  const handlePageChange = setPage;

  const handleSortChange = setSort;

  const handleOpenAddModal = () => setIsAddModal(true);

  const handleCloseAddModal = () => {
    setIsAddModal(false);
    setEditRecord(undefined);
  };

  const handleOpenModal = () => setIsShowModal(true);

  const handleCloseModal = (needToRefetch?: boolean) => {
    setIsShowModal(false);

    if (needToRefetch) {
      refetchDealflow();
    }
  };

  const handleEdit = (record: DealflowRecord) => {
    setIsAddModal(true);
    setEditRecord(record);
  };
  const populateRecords = (records: DealflowRecord[]) =>
    records.map((record) => ({
      ...record,
      company: {
        ...record.company,
        currencyData: currencies.find(
          (item: any) => item._id === record.company.currency
        ),
      },
    }));

  const finalCompaniesData = isCompaniesLoading
    ? companiesPreviousData
    : companiesData;
  const columns = populateColumns(dealflowData?.dealflow.columns || []);
  const sections = dealflowData?.dealflow.sections || [];
  const records = populateRecords(
    finalCompaniesData?.dealflow.records.nodes || []
  );
  const total = finalCompaniesData?.dealflow.records.total || 0;
  const perPage = finalCompaniesData?.dealflow.records.perPage || 0;
  const totalPages = Math.ceil(total / perPage);
  const isLoading = isDealflowLoading || isCompaniesLoading;
  const showingFrom = 1 + (page - 1) * perPage;
  const showingTo = showingFrom + records.length - 1;
  const isFiltering = Boolean(query);
  const isSearchEmpty = !isLoading && records.length === 0 && isFiltering;
  const isNoResults = !isLoading && records.length === 0 && !isFiltering;

  return {
    isLoading,
    isSearchEmpty,
    isNoResults,
    isAddModal,
    isShowModal,
    columns,
    sections,
    records,
    total,
    totalPages,
    page,
    query,
    sort,
    showingFrom,
    showingTo,
    selectedRecord,
    previewRecord,
    editRecord,
    handleSelect,
    handleQueryChange,
    handleSectionsChange,
    handleInvestorTypeChange,
    handlePageChange,
    handleSortChange,
    handleCloseSidebar,
    handleClosePreview,
    handleOpenAddModal,
    handleCloseAddModal,
    handleOpenModal,
    handleCloseModal,
    handleEdit,
    refetch,
  };
};

export default useDealflow;
